import { MAX_CHAT_IMAGES, type ImageMime } from './protocol.ts';

export { MAX_CHAT_IMAGES };

const ALLOWED = new Set<string>(['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg']);
const IMAGE_EXT = /\.(jpe?g|png|gif|webp)$/i;
const MAX_EDGE = 1920;
const MAX_PASSTHROUGH = 2 * 1024 * 1024;
const MAX_OUTPUT = 4 * 1024 * 1024;

export type PreparedImage = {
  key: string;
  mime: ImageMime;
  data: string;
  previewUrl: string;
  /** Set when this thumb was loaded from a saved task image. */
  existingId?: string;
};

export function isFileDrag(dt: DataTransfer | null): boolean {
  if (!dt) return false;
  if (Array.from(dt.types).includes('Files')) return true;
  if (dt.files.length > 0) return true;
  return [...dt.items].some((item) => item.kind === 'file');
}

function looksLikeImage(file: File): boolean {
  if (file.type) return file.type.startsWith('image/') || ALLOWED.has(file.type);
  return IMAGE_EXT.test(file.name);
}

/** Photos from a desktop drag, ignoring the PDFs and folders that come along. */
export function imageFilesFromDataTransfer(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  const fromItems = [...dt.items]
    .filter((item) => item.kind === 'file' && (!item.type || item.type.startsWith('image/')))
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null && looksLikeImage(file));
  if (fromItems.length > 0) return fromItems;
  return [...dt.files].filter(looksLikeImage);
}

function asMime(type: string): ImageMime | null {
  if (type === 'image/jpg') return 'image/jpeg';
  if (type === 'image/jpeg' || type === 'image/png' || type === 'image/gif' || type === 'image/webp') {
    return type;
  }
  return null;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result ?? '');
      const i = s.indexOf(',');
      resolve(i === -1 ? s : s.slice(i + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('could not read image'));
    reader.readAsDataURL(blob);
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('could not encode image'));
    }, type, quality);
  });
}

/**
 * Shrink a phone photo to something the gateway will accept. GIFs pass through
 * so a short capture does not become a still.
 */
export async function prepareImage(file: File): Promise<PreparedImage> {
  const claimed = asMime(file.type);
  if (file.type && !ALLOWED.has(file.type)) {
    throw new Error('use a JPEG, PNG, GIF, or WebP');
  }

  if (claimed === 'image/gif') {
    if (file.size > MAX_OUTPUT) throw new Error('that GIF is too large');
    return {
      key: crypto.randomUUID(),
      mime: 'image/gif',
      data: await blobToBase64(file),
      previewUrl: URL.createObjectURL(file),
    };
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('could not read that image');
  }

  const smallEnough =
    claimed &&
    Math.max(bitmap.width, bitmap.height) <= MAX_EDGE &&
    file.size <= MAX_PASSTHROUGH;
  if (smallEnough && claimed) {
    bitmap.close();
    return {
      key: crypto.randomUUID(),
      mime: claimed,
      data: await blobToBase64(file),
      previewUrl: URL.createObjectURL(file),
    };
  }

  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('could not read that image');
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();

  let blob = await canvasToBlob(canvas, 'image/jpeg', 0.82);
  if (blob.size > MAX_OUTPUT) blob = await canvasToBlob(canvas, 'image/jpeg', 0.55);
  if (blob.size > MAX_OUTPUT) throw new Error('that image is too large');

  return {
    key: crypto.randomUUID(),
    mime: 'image/jpeg',
    data: await blobToBase64(blob),
    previewUrl: URL.createObjectURL(blob),
  };
}

export async function prepareImages(
  files: File[],
  already: number,
): Promise<{ images: PreparedImage[]; error: string | null }> {
  const room = MAX_CHAT_IMAGES - already;
  if (room <= 0) return { images: [], error: `at most ${MAX_CHAT_IMAGES} photos` };
  const picked = files.slice(0, room);
  const images: PreparedImage[] = [];
  for (const file of picked) {
    images.push(await prepareImage(file));
  }
  const extra = files.length > room ? `at most ${MAX_CHAT_IMAGES} photos` : null;
  return { images, error: extra };
}
